import React from "react";
import PropTypes from "prop-types";
// import "./CompletedMatch.css";

// File: /e:/projects/common-admin/ybl-admin/src/components/ycl/CompletedMatch.jsx

function formatOver(o) {
    if (o === null || o === undefined) return "-";
    // If it's a float like 1.20000004 -> convert to 1.2 (one decimal)
    return Number(o).toFixed(1);
}

function formatDate(iso) {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleString();
}

const Inning = ({ inning, teamsMap }) => {
    const battingTeamName = teamsMap[inning.batting_team_id] || "Team";
    return (
        <div className="col-lg-6 col-12">
            <div className="cm-inning">
                <div className="cm-inning-header">
                    <div className="cm-inning-title">
                        {inning.inning_no}. {battingTeamName}
                    </div>
                    <div className="cm-inning-score">
                        {inning.total_runs}/{inning.wickets} ({formatOver(inning.overs)} ov)
                    </div>
                </div>

                <div className="cm-section cm-batting">
                    {/* <div className="cm-section-title">Batting</div> */}
                    <table className="cm-table">
                        <thead>
                            <tr>
                                <th>Batting</th>
                                <th>R</th>
                                <th>B</th>
                                <th>4s</th>
                                <th>6s</th>
                                <th>SR</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(inning.batting) && inning.batting.map((p) => (
                                <tr key={p.playerId}>
                                    <td className={`cm-batter-name ${p.position || ""}`}>
                                        {p.playerName}
                                        <p className="player_status_running mb-0">{p.how_out || (p.position === "not-out" ? "not out" : "-")}</p>
                                    </td>
                                    <td>{p.runs}</td>
                                    <td>{p.balls}</td>
                                    <td>{p.fours}</td>
                                    <td>{p.sixes}</td>
                                    <td>{p.strike_rate ? Number(p.strike_rate).toFixed(1) : "-"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="cm-section cm-bowling">
                    {/* <div className="cm-section-title">Bowling</div> */}
                    <table className="cm-table">
                        <thead>
                            <tr>
                                <th>BOWLER</th>
                                <th>O</th>
                                <th>M</th>
                                <th>R</th>
                                <th>W</th>
                                <th>Econ</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(inning.bowling) && inning.bowling.map((b) => (
                                <tr key={b.playerId}>
                                    <td>{b.playerName}</td>
                                    <td>{formatOver(b.overs)}</td>
                                    <td>{b.maidens}</td>
                                    <td>{b.runs_conceded}</td>
                                    <td>{b.wickets}</td>
                                    <td>{b.economy ? Number(b.economy).toFixed(2) : "-"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {Array.isArray(inning.fallOfWickets) && inning.fallOfWickets.length > 0 && (
                    <div className="cm-section cm-fall">
                        <div className="cm-section-title mb-0">Fall of Wickets</div>
                        <div className="cm-fall-list">
                            {inning.fallOfWickets.map((f, i) => (
                                <div key={i} className="cm-fall-item">
                                    {f.wicket_no}. {f.playerName} — {f.team_score} ({formatOver(f.over_at_fall)} ov)
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

Inning.propTypes = {
    inning: PropTypes.object.isRequired,
    teamsMap: PropTypes.object.isRequired,
};

export default function CompletedMatch({ scorecard }) {
    if (!scorecard || !scorecard.match) return null;

    const m = scorecard.match;
    const teamsMap = {
        [m.teamA_id]: m.teamA_name,
        [m.teamB_id]: m.teamB_name,
    };

    return (
        <div className="cm-container">
            <div className="cm-teams mb-0 d-flex justify-content-center">
                <div className="cm-team">
                    <div className="cm-team-name">{m.teamA_name}</div>
                </div>
                <div className="cm-vs">vs</div>
                <div className="cm-team">
                    <div className="cm-team-name">{m.teamB_name}</div>
                </div>
            </div>
            <div className="cm-header mb-4 d-flex justify-content-center">
                <div className="cm-left">
                    <div className="cm-match-meta">
                        <span className="cm-venue">{m.venue}</span>
                        <span className="cm-date">{formatDate(m.match_date)}</span>
                        <span className="cm-type">{m.match_type}</span>
                    </div>
                </div>
            </div>
            <div className="cm-right text-center mb-4">
                <div className={`cm-status ${m.status}`}>{m.status}</div>

                {m.status === "completed" ? (
                    <div className="cm-result fw-bold text-success">
                        {(() => {
                            const winnerTeamId = m.winner_team_id;
                            const winnerName = teamsMap[winnerTeamId];

                            const innings = scorecard.innings;
                            if (!innings || innings.length < 2) return `${winnerName} won the match`;

                            const firstInn = innings[0];
                            const secondInn = innings[1];

                            // If winner batted first: win by RUNS
                            if (firstInn.batting_team_id === winnerTeamId) {
                                const runDiff = firstInn.total_runs - secondInn.total_runs;
                                return `${winnerName} won by ${runDiff} runs`;
                            }

                            // If winner batted second: win by WICKETS
                            if (secondInn.batting_team_id === winnerTeamId) {
                                const wicketsRemaining = 10 - secondInn.wickets;
                                return `${winnerName} won by ${wicketsRemaining} wickets`;
                            }

                            return `${winnerName} won the match`;
                        })()}
                    </div>
                ) : (
                    m.toss_winner_name && (
                        <div className="cm-toss">
                            Toss: {m.toss_winner_name} elected to {m.elected_to}
                        </div>
                    )
                )}
            </div>

            <div className="cm-innings row">
                {Array.isArray(scorecard.innings) && scorecard.innings.map((inn) => (
                    <Inning key={inn.id} inning={inn} teamsMap={teamsMap} />
                ))}
            </div>
        </div>
    );
}

CompletedMatch.propTypes = {
    scorecard: PropTypes.object.isRequired,
};